<script setup lang="ts">
//#region imports
import { cloneDeep } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useActivitiesLanguage } from '@/views/CRM/composables/useActivitiesLanguage';
import { useActivitiesMailAction } from '@/views/CRM/composables/useActivitiesMailAction';
import UiIframe from '@/views/CRM/components/UiIframe.vue';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import CrmErrorPanel from '@/views/CRM/components/CrmErrorPanel.vue';
import CrmActionParams from '@/views/CRM/components/CrmActionParams.vue';
import type { CrmAction, CrmActionGroup, CrmActivity } from '@/types/crm';
import type { EmitsToListeners } from '@/types/core';
//#endregion

//#region types
interface Props {
  activity?: CrmActivity;
  currentBrand?: string;
  group?: CrmActionGroup | null;
  payload?: { actions?: CrmAction[]; index?: number } | null;
}

interface Emits {
  'send-test-email': [{ actionGroup: CrmActionGroup; index: number }];
  close: [];
}

export interface AddMailActionPreviewParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  activity: () => ({ action_groups: [] } as unknown as CrmActivity),
  currentBrand: '',
  group: null,
  payload: () => ({}),
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const store = useStore();
const { t } = useI18n();
//#endregion

//#region composables
const isVisible = ref(false);
const index = ref(0);
const actionGroup = ref<CrmActionGroup | null>(null);
const actionGroupIndex = ref(-1);
const actions = ref<CrmAction[]>([]);
const emailTemplateId = ref('');
const previewList = ref<Array<unknown> | null>(null);

const languagesFromStore = computed(() => store.state.misc?.languages || []);
const {
  languages,
  handleSelectTab,
} = useActivitiesLanguage<CrmAction>({
  actions,
  index,
  actionGroupIndex,
  languagesFromStore,
});

const {
  stateEnum,
  currentPreviewState,
  currentPreviewStateWatcherParams,
  isActionsSendDisabled,
  loadCurrentPreview,
  handleSendTestEmail: buildSendTestEmailPayload,
  getDefaultForm,
} = useActivitiesMailAction({
  actions,
  index,
  previewList,
  emailTemplateId,
  currentBrand: computed(() => props.currentBrand || ''),
  postEmailTemplatePreview: (payload) => api.postEmailTemplatePreview(payload),
});
//#endregion

//#region computed
const popupTitle = computed(() => {
  const subject = actions.value[index.value]?.email_subject;
  const suffix = subject ? ` (${subject})` : '';
  return `${t('crm.activities.actions.action_popup.mail_title')}${suffix}`;
});
//#endregion

//#region methods
function open() {
  const targetGroup = props.group;
  const incomingPayload = props.payload || {};

  if (!targetGroup) {
    isVisible.value = true;
    return;
  }

  actionGroup.value = targetGroup;
  actionGroupIndex.value = props.activity.action_groups.indexOf(targetGroup);

  const sourceActions = (props.activity.action_groups[actionGroupIndex.value]?.actions || [])
    .filter((item) => item.type === 'email');

  if (!sourceActions.length) {
    sourceActions.push(getDefaultForm() as unknown as CrmAction);
  }

  actions.value = incomingPayload?.actions
    ? cloneDeep(incomingPayload.actions)
    : cloneDeep(sourceActions);

  emailTemplateId.value = String(actions.value[0]?.email_template_id || '');
  index.value = typeof incomingPayload.index === 'number' ? incomingPayload.index : 0;
  isVisible.value = true;
  previewList.value = [];
}

function close() {
  isVisible.value = false;
  emit('close');
}

function handleSendTestEmail() {
  const payload = buildSendTestEmailPayload();
  emit('send-test-email', {
    actionGroup: {
      ...(actionGroup.value || {}),
      actions: payload.actionGroup.actions as CrmAction[],
    } as CrmActionGroup,
    index: payload.index,
  });
}
//#endregion

//#region watch
watch(currentPreviewStateWatcherParams, (value) => {
  if (value.currentPreviewState === stateEnum.not_loaded) {
    loadCurrentPreview();
  }
}, { deep: true, immediate: true });

watch(index, () => {
  if (currentPreviewState.value !== stateEnum.not_avaliable && previewList.value) {
    previewList.value = previewList.value.map((item) => {
      if (item === stateEnum.error) {
        return null;
      }
      return item;
    });
  }
}, { immediate: true });

watch(emailTemplateId, () => {
  loadCurrentPreview();
}, { immediate: true });
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="crm-add-mail-action-preview-popup"
    :title="popupTitle"
    :width="1300"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:-my-16 t:-mx-24">
        <LanguagesSetup
          :selected-index="index"
          :languages="languages"
          :can-edit="false"
          class="crm-add-mail-action-preview-popup__languages"
          @select-index="handleSelectTab"
        >
          <div class="t:flex t:flex-col t:h-full">
            <UiIframe
              v-if="currentPreviewState === stateEnum.loaded"
              :html="String(previewList?.[index] || '')"
            />
            <div
              v-else
              v-loading="true"
            />
          </div>
        </LanguagesSetup>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          icon="paper-plane"
          :disabled="isActionsSendDisabled"
          @click="handleSendTestEmail()"
        >
          {{ $t('crm.buttons.send_test_email') }}
        </UiButton>
        <UiButton
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.crm-add-mail-action-preview-popup {
  .popup__header{
    border: none;
  }
  &__languages {
    .languages-setup__content {
      height: 620px;
      max-height: calc(100vh - 150px);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
